// Tutorial chapter steps configuration
// This file contains all the step-by-step instructions for each tutorial chapter

export const CHAPTER_STEPS = {
  'chapter-1': [
    {
      id: 'welcome-ch1',
      title: 'Your Business Command Center',
      content: 'Think of this as your business headquarters—everything you need to manage your AI phone assistant in one place. We\'ll walk through each area so you understand exactly how this helps your laundry business run smoother.',
      target: null,
      position: 'center',
      action: 'Begin Tour'
    },
    {
      id: 'sidebar-overview',
      title: 'Your Business Knowledge Archive',
      content: 'This left panel stores everything your AI knows about your business—like having all your pricing sheets, service descriptions, and policies organized in one filing system. Your AI pulls answers from here when customers call.',
      target: '.brain-sidebar',
      position: 'right',
      highlight: true
    },
    {
      id: 'main-content-area',
      title: 'Your Workspace',
      content: 'This main area is where you actually build and edit your AI\'s knowledge. When you click items from the left panel, you can modify them here—like editing a price list or updating your business hours.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'filter-dropdown-show',
      title: 'Organize Your View',
      content: 'This dropdown sorts what you see in the left panel—just like organizing file cabinets by department. You can focus on just Training (customer answers), Settings (AI behavior), Integrations (connected systems), or Post Call Actions (automated follow-ups).',
      target: '.brain-sidebar',
      position: 'right',
      highlight: true,
      autoOpenDropdown: true
    },
    {
      id: 'tutorial-button-intro',
      title: 'Your Learning Center',
      content: 'This lightbulb button opens step-by-step guides for every feature. Think of it as your personal trainer—always available when you need help learning something new about your AI system.',
      target: 'button[aria-label="Start tutorial"]',
      position: 'bottom',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Open Learning Center'
    },
    {
      id: 'tutorial-modal-explain-close',
      title: 'All Your Training Modules',
      content: 'This shows every lesson available—from basic setup to advanced features. Each chapter builds on the last, so you can learn at your own pace. You can always come back to these lessons later.',
      target: '[role="dialog"], .bg-white.rounded-2xl.shadow-xl',
      position: 'center',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Close This Window'
    },
    {
      id: 'conflicts-button-intro',
      title: 'Quality Control Monitor',
      content: 'This shield button alerts you when your AI has conflicting information—like if you accidentally taught it two different prices for the same service. Catching these conflicts keeps your AI giving consistent answers to customers.',
      target: 'button[aria-label*="conflict"], button[aria-label*="detected"]',
      position: 'bottom',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Check for Conflicts'
    },
    {
      id: 'conflicts-panel-explain-close',
      title: 'Conflict Resolution Center',
      content: 'This panel shows you exactly where your AI has conflicting information and helps you fix it. It\'s like having a quality inspector that ensures your AI always gives customers the right information about your business.',
      target: '.w-full.h-full.bg-white.border-l.flex.flex-col',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Close This Panel'
    },
    {
      id: 'coverage-button-intro',
      title: 'Business Readiness Score',
      content: 'This chart button shows how well your AI can handle different types of customer questions. It\'s like a business report card—higher coverage means your AI can help more customers without transferring calls to you.',
      target: 'button[aria-label*="AI Coverage"], button[aria-label*="Coverage"]',
      position: 'bottom',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'View Your Score'
    },
    {
      id: 'coverage-panel-explain',
      title: 'Detailed Business Analytics',
      content: 'This panel breaks down exactly what types of questions your AI can and can\'t handle yet. Use this to identify areas where adding more training will help your AI handle more customer calls independently.',
      target: '.w-full.h-full.bg-white.border-l.flex.flex-col',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Close Analytics'
    },
    {
      id: 'search-functionality',
      title: 'Quick Reference Tool',
      content: 'This search bar helps you instantly find any answer, setting, or action you\'ve created—like searching through a well-organized filing system. Essential when you have dozens of responses and need to find something specific quickly.',
      target: 'input[placeholder="Search brain..."]',
      position: 'bottom',
      highlight: true,
      action: 'Continue'
    },
    {
      id: 'add-training-intro',
      title: 'Build Your AI\'s Knowledge',
      content: 'This plus button is where you add new information to your AI—answers about pricing, services, policies, anything customers ask about. Every piece you add makes your AI more capable of handling customer calls independently.',
      target: '.add-category-button',
      position: 'right',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Add New Knowledge'
    },
    {
      id: 'brain-action-modal-overview',
      title: 'Five Ways to Train Your AI',
      content: 'Here are your options: Simple Answer (basic Q&A), Complex Answer (checks your systems for live data), Simple Action (sends texts/emails), Workflow (multi-step customer service), and New Category (organizes everything). Start simple and build up.',
      target: '[data-tutorial-target="brain-action-modal"]',
      position: 'left',
      highlight: true,
      requiresAction: false,
      action: 'Understand Options'
    },
    {
      id: 'brain-action-modal-select',
      title: 'Start with Simple Answers',
      content: 'Simple Answer is perfect for most customer questions—pricing, hours, services, policies. This covers 80% of what customers call about. Click it to create your first response.',
      target: '[data-tutorial-action="simple_answer"]',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Choose Simple Answer'
    },
    {
      id: 'simple-answer-modal-explain',
      title: 'Teaching Your AI to Answer',
      content: 'This form is where you teach your AI exactly how to respond to customer questions. Think of it like training a new employee—you give them the question customers ask and the exact answer you want them to give.',
      target: '[data-tutorial-target="simple-answer-modal"]',
      position: 'left',
      highlight: true,
      requiresAction: false,
      action: 'Continue'
    },
    {
      id: 'ai-assistant-intro',
      title: 'Your Bulk Training Assistant',
      content: 'This robot button opens a powerful shortcut—instead of creating responses one by one, you can describe your entire business in plain English and it creates dozens of answers automatically. It\'s like having an expert set up your system for you.',
      target: '.ai-assistant-button',
      position: 'bottom',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Try Bulk Training'
    },
    {
      id: 'ai-chat-explain',
      title: 'Natural Language Setup',
      content: 'Here you can describe your business in everyday language—"We charge $2 per pound for wash and fold, open 7am to 9pm, pickup available"—and it automatically creates all the responses your AI needs. This saves hours of manual setup.',
      target: '.brain-chat-interface',
      position: 'left',
      highlight: true
    },
    {
      id: 'next-steps-choice',
      title: 'Choose Your Path Forward',
      content: 'You\'ve seen how everything works. Now decide: set up your AI yourself using these tools, or get a free consultation with our specialist who can help you maximize your results and get up and running faster.',
      position: 'center',
      highlight: false,
      showChoices: true,
      choices: [
        {
          id: 'continue-building',
          title: 'I\'ll Set It Up Myself',
          description: 'Use the tools to create my AI responses',
          icon: 'gear',
          primary: false
        },
        {
          id: 'schedule-call',
          title: 'Get Expert Help (Recommended)',
          description: 'Free 30-minute setup consultation',
          icon: 'calendar',
          primary: true
        }
      ]
    },
    {
      id: 'experiment-while-waiting',
      title: 'Play Around While You Wait',
      content: 'Great choice! While waiting for your expert consultation, feel free to experiment with the tools you just learned about. Try adding a simple answer about your pricing or hours - you can always modify or delete anything before your meeting. Getting hands-on experience will make your consultation even more productive.',
      target: null,
      position: 'center',
      action: 'Start Experimenting',
      showOnlyForChoice: 'schedule-call'
    },
    {
      id: 'complete-ch1',
      title: 'Tour Complete',
      content: 'Perfect! You now understand how your AI system works and where everything is located. You\'re ready to start building your AI assistant that will handle customer calls and grow your business.',
      target: null,
      position: 'center',
      action: 'Start Building'
    }
  ],

  'chapter-2': [
    {
      id: 'welcome-ch2',
      title: 'Your First Teaching - Simple Answers',
      content: 'Let\'s teach your AI the most common question every laundromat gets: "What are your prices?"',
      target: null,
      position: 'center',
      action: 'Start Chapter 2'
    },
    {
      id: 'add-category',
      title: 'Create Your First Category',
      content: 'Click the + button to create a category for organizing your AI\'s knowledge. You can name it anything you like - perhaps "Pricing & Services" or "General Information".',
      target: '.add-category-button',
      position: 'right',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Create Category'
    },
    {
      id: 'add-simple-answer',
      title: 'Add Your First Price Answer',
      content: 'Now let\'s add a Simple Answer about your wash & fold pricing. This teaches your AI to respond to pricing questions.',
      target: '[data-tutorial-action="simple_answer"]',
      position: 'right',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Add Simple Answer'
    },
    {
      id: 'modal-overview',
      title: 'Simple Answer Creation Form',
      content: 'This is where you\'ll teach your AI how to respond. Let\'s fill out each section step by step.',
      target: '[data-tutorial-target="simple-answer-modal"]',
      position: 'center',
      highlight: true
    },
    {
      id: 'fill-section-title',
      title: 'Enter the Section Title',
      content: 'Type a question your customers might ask, like "What are your wash and fold prices?"',
      target: 'input[name="question"]',
      position: 'right',
      highlight: true,
      requiresAction: true,
      actionType: 'type',
      minCharacters: 1,
      action: 'Type Question'
    },
    {
      id: 'fill-section-content',
      title: 'Add Your Answer Content',
      content: 'Now type your response, like "Our wash & fold service is $1.75 per pound with a 10-pound minimum."',
      target: 'textarea[name="answer"]',
      position: 'right',
      highlight: true,
      requiresAction: true,
      actionType: 'type',
      minCharacters: 1,
      action: 'Type Answer'
    },
    {
      id: 'select-category-trigger',
      title: 'Open Category Dropdown',
      content: 'Click on the category dropdown to see the available options.',
      target: '[data-testid="select-trigger"], .select-trigger, [role="combobox"]',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Open Dropdown'
    },
    {
      id: 'select-category-option',
      title: 'Choose Any Category',
      content: 'Click on any category from the dropdown options to organize this answer. You can choose any existing category or the one you just created.',
      target: '[data-testid="select-option"], .select-option, [role="option"]',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Select Category'
    },
    {
      id: 'create-answer',
      title: 'Save Your Answer',
      content: 'Click the Create button to save your first AI response. Your AI now knows how to answer pricing questions!',
      target: '[data-tutorial-target="create-button"]',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Create Answer'
    },
    {
      id: 'complete-ch2',
      title: 'Chapter 2 Complete!',
      content: 'Excellent! Your AI now knows your pricing. You can add more questions like hours, services, and policies the same way.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 2'
    }
  ],

  'chapter-3': [
    {
      id: 'welcome-ch3',
      title: 'Location Intelligence - Multiple Stores',
      content: 'If you have multiple locations, your AI can give different responses for each store. Let\'s set this up.',
      target: null,
      position: 'center',
      action: 'Start Chapter 3'
    },
    {
      id: 'select-setting',
      title: 'Select a Setting to Modify',
      content: 'Choose any existing answer or action from your sidebar. We\'ll add location-specific versions.',
      target: '.brain-sidebar',
      position: 'right',
      highlight: true
    },
    {
      id: 'add-location-variant',
      title: 'Add Location Version',
      content: 'Look for the location pin icon and click "Add Location Version". This creates different responses for different stores.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch3',
      title: 'Chapter 3 Complete!',
      content: 'Perfect! Your AI now gives location-specific responses. Customers automatically get the right information for their store.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 3'
    }
  ],

  'chapter-4': [
    {
      id: 'welcome-ch4',
      title: 'Complex Answers - Real-Time Lookups',
      content: 'Let\'s create dynamic responses that check live data, like "Is my order ready?" or "What machines are available?"',
      target: null,
      position: 'center',
      action: 'Start Chapter 4'
    },
    {
      id: 'add-complex-answer',
      title: 'Create a Complex Answer',
      content: 'Click the + button and select "Complex Answer". This type can check real-time data from your systems.',
      target: '.add-category-button',
      position: 'right',
      highlight: true,
      action: 'Add Complex Answer'
    },
    {
      id: 'configure-lookup',
      title: 'Configure Data Lookup',
      content: 'Set up questions the AI will ask customers, then connect to your POS system to check order status in real-time.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch4',
      title: 'Chapter 4 Complete!',
      content: 'Excellent! Your AI can now check live data. Customers can get real-time order status and machine availability.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 4'
    }
  ],

  'chapter-5': [
    {
      id: 'welcome-ch5',
      title: 'Simple Actions - Making Things Happen',
      content: 'Let\'s make your AI do things beyond just answering. We\'ll set up SMS notifications and email actions.',
      target: null,
      position: 'center',
      action: 'Start Chapter 5'
    },
    {
      id: 'add-simple-action',
      title: 'Create an SMS Action',
      content: 'Click + and select "Simple Action". Choose SMS to send text notifications when orders are ready.',
      target: '.add-category-button',
      position: 'right',
      highlight: true,
      action: 'Add Simple Action'
    },
    {
      id: 'configure-sms',
      title: 'Set Up SMS Notifications',
      content: 'Configure the message template and when to send it. Your AI can now notify customers automatically.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch5',
      title: 'Chapter 5 Complete!',
      content: 'Great! Your AI can now take actions. Customers can request notifications and your AI will send them automatically.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 5'
    }
  ],

  'chapter-6': [
    {
      id: 'welcome-ch6',
      title: 'Workflows - Complete Customer Journeys',
      content: 'Build complex multi-step processes like pickup scheduling that guide customers through complete interactions.',
      target: null,
      position: 'center',
      action: 'Start Chapter 6'
    },
    {
      id: 'add-workflow',
      title: 'Create a Workflow',
      content: 'Click + and select "Workflow". This creates multi-step processes that can handle complex customer requests.',
      target: '.add-category-button',
      position: 'right',
      highlight: true,
      action: 'Add Workflow'
    },
    {
      id: 'design-flow',
      title: 'Design Your Customer Journey',
      content: 'Build a step-by-step process: verify service area → choose service type → select pickup time → confirm details.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch6',
      title: 'Chapter 6 Complete!',
      content: 'Amazing! Your AI can now handle complex customer journeys from start to finish.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 6'
    }
  ],

  'chapter-7': [
    {
      id: 'welcome-ch7',
      title: 'Integrations - Connecting Your Systems',
      content: 'Connect your POS, payment systems, and other business tools to make your AI even more powerful.',
      target: null,
      position: 'center',
      action: 'Start Chapter 7'
    },
    {
      id: 'open-integrations',
      title: 'Browse Available Integrations',
      content: 'Click the + button next to "Integrations" to see what systems you can connect.',
      target: '.integrations-add-button',
      position: 'right',
      highlight: true,
      action: 'View Integrations'
    },
    {
      id: 'connect-system',
      title: 'Connect Your POS System',
      content: 'Select your POS system (like Curbside) and follow the connection steps. This gives your AI access to real data.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch7',
      title: 'Chapter 7 Complete!',
      content: 'Perfect! Your AI is now connected to your business systems and can access real-time data.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 7'
    }
  ],

  'chapter-8': [
    {
      id: 'welcome-ch8',
      title: 'Post-Call Actions - Automatic Follow-ups',
      content: 'Set up tasks that happen automatically after customer calls, like sending confirmations or surveys.',
      target: null,
      position: 'center',
      action: 'Start Chapter 8'
    },
    {
      id: 'add-post-call-action',
      title: 'Create a Post-Call Action',
      content: 'Click + next to "Post Call Actions" to set up automatic follow-ups after calls end.',
      target: '.post-call-add-button',
      position: 'right',
      highlight: true,
      action: 'Add Post-Call Action'
    },
    {
      id: 'configure-followup',
      title: 'Configure Automatic Follow-up',
      content: 'Set up an email confirmation that sends automatically when orders are placed.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch8',
      title: 'Chapter 8 Complete!',
      content: 'Excellent! Your AI now handles follow-ups automatically, improving customer experience.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 8'
    }
  ],

  'chapter-9': [
    {
      id: 'welcome-ch9',
      title: 'The AI Assistant - Bulk Operations Magic',
      content: 'Learn to use natural language to create multiple responses at once. This is the fastest way to train your AI.',
      target: null,
      position: 'center',
      action: 'Start Chapter 9'
    },
    {
      id: 'open-ai-assistant',
      title: 'Open Your AI Assistant',
      content: 'Click the Android icon to open your AI assistant. This is where the magic happens - you can describe what you want and it creates everything.',
      target: '.ai-assistant-button',
      position: 'left',
      highlight: true,
      requiresAction: true,
      actionType: 'click',
      action: 'Open AI Assistant'
    },
    {
      id: 'natural-language',
      title: 'Describe What You Want',
      content: 'Try typing: "Create answers for our laundry services: wash & fold $1.75/lb, dry cleaning shirts $2.99, and we\'re open 7am-9pm daily"',
      target: '.brain-chat-interface',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch9',
      title: 'Chapter 9 Complete!',
      content: 'Amazing! You can now create dozens of responses in seconds using natural language. This is the power of AI-assisted training.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 9'
    }
  ],

  'chapter-10': [
    {
      id: 'welcome-ch10',
      title: 'Conflict Detection - Consistency is Key',
      content: 'Learn to identify and resolve conflicting information to keep your AI responses consistent.',
      target: null,
      position: 'center',
      action: 'Start Chapter 10'
    },
    {
      id: 'view-conflicts',
      title: 'Check for Conflicts',
      content: 'Click the shield icon to see if your AI has any conflicting information that needs to be resolved.',
      target: '.conflicts-button',
      position: 'right',
      highlight: true,
      action: 'View Conflicts'
    },
    {
      id: 'resolve-conflicts',
      title: 'Resolve Inconsistencies',
      content: 'Review any conflicts and update your responses to ensure your AI gives consistent information.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch10',
      title: 'Chapter 10 Complete!',
      content: 'Perfect! Your AI now gives consistent, reliable information to all customers.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 10'
    }
  ],

  'chapter-11': [
    {
      id: 'welcome-ch11',
      title: 'AI Coverage Analysis - Industry Specific',
      content: 'Monitor and improve your AI\'s knowledge coverage to ensure it can handle all customer questions.',
      target: null,
      position: 'center',
      action: 'Start Chapter 11'
    },
    {
      id: 'view-coverage',
      title: 'Check AI Coverage',
      content: 'Click the bar chart icon to see how well your AI covers different types of customer questions.',
      target: '.coverage-button',
      position: 'right',
      highlight: true,
      action: 'View Coverage'
    },
    {
      id: 'improve-coverage',
      title: 'Improve Knowledge Gaps',
      content: 'Review areas where your AI needs more training and add responses for uncovered questions.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch11',
      title: 'Chapter 11 Complete!',
      content: 'Excellent! You can now monitor and continuously improve your AI\'s knowledge coverage.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 11'
    }
  ],

  'chapter-12': [
    {
      id: 'welcome-ch12',
      title: 'Advanced Laundry Features',
      content: 'Set up seasonal services, commercial vs residential pricing, and route optimization features.',
      target: null,
      position: 'center',
      action: 'Start Chapter 12'
    },
    {
      id: 'seasonal-services',
      title: 'Create Seasonal Services',
      content: 'Add special services for different seasons: winter comforter cleaning, spring wedding dress preservation.',
      target: '.add-category-button',
      position: 'right',
      highlight: true
    },
    {
      id: 'commercial-pricing',
      title: 'Set Up Commercial Pricing',
      content: 'Create different pricing tiers for commercial accounts vs residential customers.',
      target: '.brain-main-content',
      position: 'left',
      highlight: true
    },
    {
      id: 'complete-ch12',
      title: 'Chapter 12 Complete!',
      content: 'Great! Your AI now handles advanced features specific to the laundry industry.',
      target: null,
      position: 'center',
      action: 'Complete Chapter 12'
    }
  ],

  'chapter-13': [
    {
      id: 'welcome-ch13',
      title: 'Real Laundromat Scenarios',
      content: 'See three complete implementation examples for different types of laundry businesses.',
      target: null,
      position: 'center',
      action: 'Start Chapter 13'
    },
    {
      id: 'urban-laundromat',
      title: 'Scenario 1: Busy Urban Laundromat',
      content: 'Learn how to set up express services, machine availability checking, and digital receipts.',
      target: '.brain-main-content',
      position: 'center',
      highlight: true
    },
    {
      id: 'full-service-cleaner',
      title: 'Scenario 2: Full-Service Dry Cleaner',
      content: 'Configure garment care, bulk order quotes, and wedding dress preservation workflows.',
      target: '.brain-main-content',
      position: 'center',
      highlight: true
    },
    {
      id: 'pickup-delivery',
      title: 'Scenario 3: Pickup & Delivery Service',
      content: 'Set up complete pickup scheduling, route management, and delivery confirmations.',
      target: '.brain-main-content',
      position: 'center',
      highlight: true
    },
    {
      id: 'complete-ch13',
      title: 'Congratulations! 🎉',
      content: 'You\'ve completed all tutorial chapters! You\'re now a Laundry AI expert. Your AI assistant is ready to transform your business.',
      target: null,
      position: 'center',
      action: 'Become an Expert'
    }
  ]
}; 